/**
 * Recurring Transactions Service
 * Subscribes to Plaid recurring transaction data stored in Firestore.
 *
 * Firestore path: plaidRecurringTransactions (top-level, uid field per doc)
 */

import {
  collection,
  query,
  where,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { RecurringTransaction, RecurringFrequency, RecurringStatus } from '../types';

// ============================================
// COLLECTION REFERENCES
// ============================================

const getRecurringTransactionsQuery = (uid: string) =>
  query(
    collection(db, 'plaidRecurringTransactions'),
    where('uid', '==', uid)
  );

// ============================================
// HELPERS
// ============================================

/** Parse a Firestore Timestamp or date string to UTC ISO string */
const parseDate = (d: any): string | undefined => {
  if (!d) return undefined;
  if (typeof d === 'string') {
    if (d.includes('T') && d.includes('Z')) return d;
    return new Date(d).toISOString();
  }
  if (typeof d === 'number') return new Date(d).toISOString();
  if (typeof d === 'object') {
    if (typeof d.toDate === 'function') return d.toDate().toISOString();
    if ('seconds' in d) return new Date(d.seconds * 1000).toISOString();
  }
  return undefined;
};

/** Extract numeric amount from Plaid amount map {amount: 9.99, iso_currency_code: 'USD'} or raw number */
const extractAmount = (val: any): number => {
  if (typeof val === 'number') return val;
  if (typeof val === 'object' && val !== null && 'amount' in val) {
    return typeof val.amount === 'number' ? val.amount : 0;
  }
  return 0;
};

/** Extract ISO currency code from Plaid amount map */
const extractCurrency = (val: any): string | undefined => {
  if (typeof val === 'object' && val !== null && 'iso_currency_code' in val) {
    return val.iso_currency_code;
  }
  return undefined;
};

// Pure arithmetic lives in ./transactions/recurringMath (no Firebase import)
// and is re-exported here for existing callers.
export {
  calculateMonthlyAmount,
  calculateMonthlyRecurringTotal,
  formatFrequency,
  getDisplayAmount,
} from './transactions/recurringMath';
import { getDisplayAmount } from './transactions/recurringMath';

// ============================================
// SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to all recurring transactions for a user (real-time).
 * Only returns active (non-TOMBSTONED) outflow subscriptions.
 */
export const subscribeToRecurringTransactions = (
  uid: string,
  callback: (transactions: RecurringTransaction[]) => void
): Unsubscribe => {
  const q = getRecurringTransactionsQuery(uid);

  return onSnapshot(
    q,
    (snapshot) => {
      const transactions: RecurringTransaction[] = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();

          const frequency: RecurringFrequency =
            (['WEEKLY', 'BIWEEKLY', 'SEMI_MONTHLY', 'MONTHLY', 'ANNUALLY'] as RecurringFrequency[])
              .includes(data.frequency) ? data.frequency : 'UNKNOWN';

          const status: RecurringStatus =
            (['MATURE', 'EARLY_DETECTION', 'TOMBSTONED'] as RecurringStatus[])
              .includes(data.status) ? data.status : 'MATURE';

          const pfc = data.personal_finance_category;

          return {
            id: docSnap.id,
            uid: data.uid,
            accountId: data.account_id || undefined,
            streamId: data.stream_id || undefined,
            merchantName: data.merchant_name || data.description || undefined,
            description: data.description || undefined,
            frequency,
            status,
            averageAmount: Math.abs(extractAmount(data.average_amount)),
            lastAmount: Math.abs(extractAmount(data.last_amount)),
            isoCurrencyCode: extractCurrency(data.average_amount) || extractCurrency(data.last_amount) || 'USD',
            categoryPrimary: typeof pfc === 'object' ? pfc?.primary : undefined,
            categoryDetailed: typeof pfc === 'object' ? pfc?.detailed : undefined,
            firstDate: parseDate(data.first_date),
            lastDate: parseDate(data.last_date),
            predictedNextDate: parseDate(data.predicted_next_date),
            isActive: status !== 'TOMBSTONED',
          } satisfies RecurringTransaction;
        })
        .filter(t => t.isActive); // Only return active subscriptions

      // Sort by amount descending
      transactions.sort((a, b) => getDisplayAmount(b) - getDisplayAmount(a));

      callback(transactions);
    },
    (error) => {
      console.error('Error subscribing to recurring transactions:', error);
      callback([]);
    }
  );
};
